'use client'

import { useMemo, useState, type ReactNode } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  AlertTriangle,
  Coins,
  Info,
  Landmark,
  LineChart,
  PieChart as PieChartIcon,
  Pencil,
  PencilLine,
  Plus,
  Search,
  TrendingUp,
  Trash2,
  Wallet,
  X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { InfoPopover } from '@/components/patterns/infoPopover'
import { ErrorBoundary } from '@/components/errors/error-boundary'
import { MetricCard } from '@/features/cases/components/stat-card'
import { formatCurrency, formatHeadlineCurrency } from '@/lib/format/currency'
import { formatDate } from '@/lib/format/date'
import {
  addHoldingSchema,
  addTradeSchema,
  bulkHoldingsSchema,
  bulkTradesSchema,
  createCaseHoldingSchema,
  createCaseTradeSchema,
  editHoldingSchema,
  editTradeSchema,
  type AddHoldingFormValues,
  type AddTradeFormValues,
  type BulkHoldingsFormValues,
  type BulkTradesFormValues,
  type EditHoldingFormValues,
  type EditTradeFormValues,
} from '@/features/cases/schemas/case-forms.schema'
import type {
  CaseHoldingCategory,
  CaseRecord,
  CaseTradeSegment,
  CaseTradeSide,
  CreateCaseHoldingInput,
  CreateCaseTradeInput,
  Holding,
  HoldingCategory,
  Trade,
  TradeSegment,
  UpdateCaseHoldingInput,
  UpdateCaseTradeInput,
} from '@/features/cases/types'

/** Reverse of `client-detail.tsx`'s `HOLDING_CATEGORY_MAP`/`TRADE_SEGMENT_MAP` — converts this
 * file's mock-shaped form values back into the real backend's lowercase category/segment/side
 * naming, for the one real (staff-editable) caller. */
const REVERSE_HOLDING_CATEGORY: Record<HoldingCategory, CaseHoldingCategory> = {
  Equity: 'equity',
  Debt: 'debt',
  Hybrid: 'hybrid',
  Gold: 'gold',
}
const REVERSE_TRADE_SEGMENT: Record<TradeSegment, CaseTradeSegment> = {
  Equity: 'equity',
  'F&O': 'fno',
  Commodity: 'commodity',
}
const REVERSE_TRADE_SIDE: Record<'BUY' | 'SELL', CaseTradeSide> = { BUY: 'buy', SELL: 'sell' }

// react-hook-form's `useFieldArray` needs an object-shaped form root, not a bare array — wraps
// the exported `bulkHoldingsSchema` in `{ rows: [...] }` purely for the form itself; each row's
// validation still comes straight from `bulkHoldingsSchema`'s own `addHoldingSchema` per-item rule.
const bulkHoldingsFormSchema = z.object({ rows: bulkHoldingsSchema })

// Same reasoning as `bulkHoldingsFormSchema` above — `useFieldArray` needs an object-shaped root.
const bulkTradesFormSchema = z.object({ rows: bulkTradesSchema })

function toCreateCaseHoldingInput(values: AddHoldingFormValues): CreateCaseHoldingInput {
  return createCaseHoldingSchema.parse({
    scheme: values.scheme,
    category: REVERSE_HOLDING_CATEGORY[values.category],
    units: values.units,
    investedValue: values.investedValue,
    currentValue: values.currentValue,
    xirr: values.xirr,
  })
}

function toCreateCaseTradeInput(values: AddTradeFormValues): CreateCaseTradeInput {
  return createCaseTradeSchema.parse({
    symbol: values.symbol,
    segment: REVERSE_TRADE_SEGMENT[values.segment],
    side: REVERSE_TRADE_SIDE[values.side],
    qty: values.qty,
    price: values.price,
    pnl: values.pnl,
    tradedAt: values.tradedAt,
  })
}

const pct = (n: number) => `${n.toFixed(1)}%`

/** Above this share in a single scheme, an advisor should be talking about concentration risk. */
const CONCENTRATION_THRESHOLD = 35

const CATEGORY_COLOR: Record<HoldingCategory, string> = {
  Equity: 'var(--chart-1)',
  Debt: 'var(--chart-2)',
  Hybrid: 'var(--chart-3)',
  Gold: 'var(--chart-4)',
}

const CATEGORY_ICON: Record<HoldingCategory, typeof LineChart> = {
  Equity: LineChart,
  Debt: Landmark,
  Hybrid: PieChartIcon,
  Gold: Coins,
}

const HOLDING_CATEGORIES: HoldingCategory[] = ['Equity', 'Debt', 'Hybrid', 'Gold']
const TRADE_SEGMENTS: TradeSegment[] = ['Equity', 'F&O', 'Commodity']

const SEGMENT_COLOR: Record<TradeSegment, string> = {
  Equity: 'var(--chart-1)',
  'F&O': 'var(--chart-2)',
  Commodity: 'var(--chart-3)',
}

function Signed({
  value,
  asPercent = false,
  compact = false,
}: {
  value: number
  asPercent?: boolean
  /** Abbreviated ("1.2L") rather than exact — only for a summary metric card, never a line item. */
  compact?: boolean
}) {
  const positive = value >= 0
  const abs = Math.abs(value)
  const display = asPercent
    ? `${abs.toFixed(1)}%`
    : compact
      ? formatHeadlineCurrency(abs)
      : formatCurrency(abs)
  return (
    <span className={positive ? 'text-green-600' : 'text-red-600'}>
      {positive ? '+' : '−'}
      {display}
    </span>
  )
}

/** The exact, full-precision text for a signed currency value — pairs with `<Signed compact />` as
 * the `MetricCard` `title` tooltip, same convention `audit-hero.tsx`/`trades-hero.tsx` use. */
function signedTitle(value: number): string {
  return `${value >= 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}`
}

/**
 * Adapter onto the shared `MetricCard` so these figures match the investor dashboard exactly. Kept
 * as a wrapper rather than swapping every call site: it takes the icon as a component and `sub`
 * where MetricCard takes a node and `footnote`, and there are a dozen of these across the file.
 */
function Metric({
  label,
  value,
  sub,
  icon: Icon,
  iconAccent,
  info,
  title,
}: {
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  icon: typeof Wallet
  /** Tints the icon tile — e.g. `'var(--chart-1)'`. */
  iconAccent: string
  /** Small (i) tooltip explaining what this figure is. */
  info?: string
  /** Exact-value tooltip on the figure itself, for when `value` is abbreviated. */
  title?: string
}) {
  return (
    <MetricCard
      icon={<Icon />}
      tint={iconAccent}
      label={label}
      value={value}
      footnote={sub}
      info={info}
      title={title}
    />
  )
}

/** Category-colored letter avatar — this app doesn't ship real AMC/symbol logo assets, so every
 * scheme/symbol falls back to its initial on a tinted circle. Shared by the mobile card and desktop
 * table so both stay visually consistent. */
function SchemeMark({
  initial,
  color,
  size = 9,
}: {
  initial: string
  color: string
  size?: 7 | 9
}) {
  const sizeClass = size === 7 ? 'size-7' : 'size-9'
  return (
    <span
      className={`flex ${sizeClass} shrink-0 items-center justify-center rounded-full text-sm font-bold text-white`}
      style={{ backgroundColor: color }}
      aria-hidden="true"
    >
      {initial}
    </span>
  )
}

function NoData({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <Empty className="border border-dashed py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Landmark />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {action && <EmptyContent>{action}</EmptyContent>}
    </Empty>
  )
}

/** Staff key in what they see in the uploaded CAS, line by line — this is what turns a submitted request into real analysis. */
function AddHoldingDialog({
  onSubmit: onSubmitProp,
  open,
  onOpenChange,
}: {
  /** Decoupled from any particular data source — the parent resolves this to either the real
   * mutation or the mock one, depending on whether it was given an injected callback. */
  onSubmit: (values: AddHoldingFormValues) => Promise<void>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const form = useForm<z.input<typeof addHoldingSchema>, unknown, AddHoldingFormValues>({
    resolver: zodResolver(addHoldingSchema),
    mode: 'onTouched',
    defaultValues: {
      scheme: '',
      category: 'Equity',
      units: 0,
      investedValue: 0,
      currentValue: 0,
      xirr: 0,
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await onSubmitProp(values)
    form.reset()
    onOpenChange(false)
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) form.reset()
      }}
    >
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Add holding</DialogTitle>
            <DialogDescription>
              Key in what you see in the CAS — one scheme at a time.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="h-scheme">Scheme</FieldLabel>
              <Input
                id="h-scheme"
                placeholder="e.g. Parag Parikh Flexi Cap Fund"
                {...form.register('scheme')}
              />
              <FieldError errors={[form.formState.errors.scheme]} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="h-category">Category</FieldLabel>
                <Select
                  value={form.watch('category')}
                  onValueChange={(v) => form.setValue('category', v as HoldingCategory)}
                >
                  <SelectTrigger id="h-category" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOLDING_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="h-units">Units</FieldLabel>
                <Input id="h-units" type="number" step="any" {...form.register('units')} />
                <FieldError errors={[form.formState.errors.units]} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="h-invested">Invested value (₹)</FieldLabel>
                <Input
                  id="h-invested"
                  type="number"
                  step="any"
                  {...form.register('investedValue')}
                />
                <FieldError errors={[form.formState.errors.investedValue]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="h-current">Current value (₹)</FieldLabel>
                <Input id="h-current" type="number" step="any" {...form.register('currentValue')} />
                <FieldError errors={[form.formState.errors.currentValue]} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="h-xirr">XIRR (%)</FieldLabel>
              <Input id="h-xirr" type="number" step="any" {...form.register('xirr')} />
              <FieldError errors={[form.formState.errors.xirr]} />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Adding…' : 'Add holding'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Same field set/layout as `AddHoldingDialog`, seeded from the holding being edited via RHF's
 * reactive `values:` option (the `CaseDetailsDialog`/`SaveInvestorProfileDialog` pattern) — a
 * different holding passed in re-syncs the form automatically. */
function EditHoldingDialog({
  holding,
  onSubmit: onSubmitProp,
  open,
  onOpenChange,
}: {
  holding: Holding | null
  onSubmit: (id: string, values: EditHoldingFormValues) => Promise<void>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const form = useForm<z.input<typeof editHoldingSchema>, unknown, EditHoldingFormValues>({
    resolver: zodResolver(editHoldingSchema),
    mode: 'onTouched',
    values: holding
      ? {
          scheme: holding.scheme,
          category: holding.category,
          units: holding.units,
          investedValue: holding.investedValue,
          currentValue: holding.currentValue,
          xirr: holding.xirr,
        }
      : { scheme: '', category: 'Equity', units: 0, investedValue: 0, currentValue: 0, xirr: 0 },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    if (!holding) return
    await onSubmitProp(holding.id, values)
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Edit holding</DialogTitle>
            <DialogDescription>Update this scheme&apos;s details.</DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="eh-scheme">Scheme</FieldLabel>
              <Input
                id="eh-scheme"
                placeholder="e.g. Parag Parikh Flexi Cap Fund"
                {...form.register('scheme')}
              />
              <FieldError errors={[form.formState.errors.scheme]} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="eh-category">Category</FieldLabel>
                <Select
                  value={form.watch('category')}
                  onValueChange={(v) => form.setValue('category', v as HoldingCategory)}
                >
                  <SelectTrigger id="eh-category" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOLDING_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="eh-units">Units</FieldLabel>
                <Input id="eh-units" type="number" step="any" {...form.register('units')} />
                <FieldError errors={[form.formState.errors.units]} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="eh-invested">Invested value (₹)</FieldLabel>
                <Input
                  id="eh-invested"
                  type="number"
                  step="any"
                  {...form.register('investedValue')}
                />
                <FieldError errors={[form.formState.errors.investedValue]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="eh-current">Current value (₹)</FieldLabel>
                <Input
                  id="eh-current"
                  type="number"
                  step="any"
                  {...form.register('currentValue')}
                />
                <FieldError errors={[form.formState.errors.currentValue]} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="eh-xirr">XIRR (%)</FieldLabel>
              <Input id="eh-xirr" type="number" step="any" {...form.register('xirr')} />
              <FieldError errors={[form.formState.errors.xirr]} />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const BULK_HOLDING_ROW_DEFAULT: AddHoldingFormValues = {
  scheme: '',
  category: 'Equity',
  units: 0,
  investedValue: 0,
  currentValue: 0,
  xirr: 0,
}

/** The whole sheet retyped/re-pasted, not diffed row by row — matches `PUT .../holdings`'s own
 * replace-all contract exactly. An inline-editable table rather than a form dialog, since this is
 * the one holdings UI that edits many rows in a single save. */
function BulkEditHoldingsDialog({
  holdings,
  onSubmit: onSubmitProp,
  open,
  onOpenChange,
}: {
  holdings: Holding[]
  onSubmit: (values: CreateCaseHoldingInput[]) => Promise<void>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const form = useForm<
    z.input<typeof bulkHoldingsFormSchema>,
    unknown,
    { rows: BulkHoldingsFormValues }
  >({
    resolver: zodResolver(bulkHoldingsFormSchema),
    mode: 'onTouched',
    values: {
      rows: holdings.map((h) => ({
        scheme: h.scheme,
        category: h.category,
        units: h.units,
        investedValue: h.investedValue,
        currentValue: h.currentValue,
        xirr: h.xirr,
      })),
    },
  })
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'rows' })

  const onSubmit = form.handleSubmit(async ({ rows }) => {
    await onSubmitProp(rows.map(toCreateCaseHoldingInput))
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Bulk edit holdings</DialogTitle>
            <DialogDescription>
              Replaces the entire holdings sheet with exactly what&apos;s below — add, remove, or
              change any row.
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto py-4">
            {fields.map((field, index) => {
              const rowErrors = form.formState.errors.rows?.[index]
              return (
                <div key={field.id} className="flex flex-col gap-2 rounded-lg border p-3">
                  <div className="flex items-start gap-2">
                    <Field className="flex-1">
                      <FieldLabel htmlFor={`bh-scheme-${field.id}`}>Scheme</FieldLabel>
                      <Input
                        id={`bh-scheme-${field.id}`}
                        placeholder="Scheme name"
                        {...form.register(`rows.${index}.scheme`)}
                      />
                      <FieldError errors={[rowErrors?.scheme]} />
                    </Field>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-muted-foreground mt-6 shrink-0"
                      aria-label="Remove row"
                      onClick={() => remove(index)}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bh-category-${field.id}`}>Category</FieldLabel>
                      <Select
                        value={form.watch(`rows.${index}.category`)}
                        onValueChange={(v) =>
                          form.setValue(`rows.${index}.category`, v as HoldingCategory)
                        }
                      >
                        <SelectTrigger id={`bh-category-${field.id}`} className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {HOLDING_CATEGORIES.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bh-units-${field.id}`}>Units</FieldLabel>
                      <Input
                        id={`bh-units-${field.id}`}
                        type="number"
                        step="any"
                        {...form.register(`rows.${index}.units`)}
                      />
                      <FieldError errors={[rowErrors?.units]} />
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bh-invested-${field.id}`}>Invested</FieldLabel>
                      <Input
                        id={`bh-invested-${field.id}`}
                        type="number"
                        step="any"
                        {...form.register(`rows.${index}.investedValue`)}
                      />
                      <FieldError errors={[rowErrors?.investedValue]} />
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bh-current-${field.id}`}>Current</FieldLabel>
                      <Input
                        id={`bh-current-${field.id}`}
                        type="number"
                        step="any"
                        {...form.register(`rows.${index}.currentValue`)}
                      />
                      <FieldError errors={[rowErrors?.currentValue]} />
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bh-xirr-${field.id}`}>XIRR (%)</FieldLabel>
                      <Input
                        id={`bh-xirr-${field.id}`}
                        type="number"
                        step="any"
                        {...form.register(`rows.${index}.xirr`)}
                      />
                      <FieldError errors={[rowErrors?.xirr]} />
                    </Field>
                  </div>
                </div>
              )
            })}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="self-start"
              onClick={() => append(BULK_HOLDING_ROW_DEFAULT)}
            >
              <Plus className="size-3.5" /> Add row
            </Button>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Saving…' : 'Save all'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AddTradeDialog({
  onSubmit: onSubmitProp,
  open,
  onOpenChange,
}: {
  onSubmit: (values: AddTradeFormValues) => Promise<void>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const form = useForm<z.input<typeof addTradeSchema>, unknown, AddTradeFormValues>({
    resolver: zodResolver(addTradeSchema),
    mode: 'onTouched',
    defaultValues: {
      symbol: '',
      segment: 'Equity',
      side: 'BUY',
      qty: 0,
      price: 0,
      tradedAt: '',
      pnl: 0,
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await onSubmitProp(values)
    form.reset()
    onOpenChange(false)
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) form.reset()
      }}
    >
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Add trade</DialogTitle>
            <DialogDescription>
              Key in what you see in the tradebook — one trade at a time.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="t-symbol">Symbol</FieldLabel>
                <Input id="t-symbol" placeholder="e.g. RELIANCE" {...form.register('symbol')} />
                <FieldError errors={[form.formState.errors.symbol]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="t-segment">Segment</FieldLabel>
                <Select
                  value={form.watch('segment')}
                  onValueChange={(v) => form.setValue('segment', v as TradeSegment)}
                >
                  <SelectTrigger id="t-segment" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRADE_SEGMENTS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="t-side">Side</FieldLabel>
                <Select
                  value={form.watch('side')}
                  onValueChange={(v) => form.setValue('side', v as 'BUY' | 'SELL')}
                >
                  <SelectTrigger id="t-side" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY">BUY</SelectItem>
                    <SelectItem value="SELL">SELL</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="t-qty">Quantity</FieldLabel>
                <Input id="t-qty" type="number" step="any" {...form.register('qty')} />
                <FieldError errors={[form.formState.errors.qty]} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="t-price">Price (₹)</FieldLabel>
                <Input id="t-price" type="number" step="any" {...form.register('price')} />
                <FieldError errors={[form.formState.errors.price]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="t-date">Traded on</FieldLabel>
                <Input id="t-date" type="date" {...form.register('tradedAt')} />
                <FieldError errors={[form.formState.errors.tradedAt]} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="t-pnl">P&amp;L (₹, negative for a loss)</FieldLabel>
              <Input id="t-pnl" type="number" step="any" {...form.register('pnl')} />
              <FieldError errors={[form.formState.errors.pnl]} />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Adding…' : 'Add trade'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditTradeDialog({
  trade,
  onSubmit: onSubmitProp,
  open,
  onOpenChange,
}: {
  trade: Trade | null
  onSubmit: (id: string, values: EditTradeFormValues) => Promise<void>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const form = useForm<z.input<typeof editTradeSchema>, unknown, EditTradeFormValues>({
    resolver: zodResolver(editTradeSchema),
    mode: 'onTouched',
    values: trade
      ? {
          symbol: trade.symbol,
          segment: trade.segment,
          side: trade.side,
          qty: trade.qty,
          price: trade.price,
          tradedAt: trade.tradedAt,
          pnl: trade.pnl,
        }
      : { symbol: '', segment: 'Equity', side: 'BUY', qty: 0, price: 0, tradedAt: '', pnl: 0 },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    if (!trade) return
    await onSubmitProp(trade.id, values)
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Edit trade</DialogTitle>
            <DialogDescription>Update this trade&apos;s details.</DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="et-symbol">Symbol</FieldLabel>
                <Input id="et-symbol" placeholder="e.g. RELIANCE" {...form.register('symbol')} />
                <FieldError errors={[form.formState.errors.symbol]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="et-segment">Segment</FieldLabel>
                <Select
                  value={form.watch('segment')}
                  onValueChange={(v) => form.setValue('segment', v as TradeSegment)}
                >
                  <SelectTrigger id="et-segment" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRADE_SEGMENTS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="et-side">Side</FieldLabel>
                <Select
                  value={form.watch('side')}
                  onValueChange={(v) => form.setValue('side', v as 'BUY' | 'SELL')}
                >
                  <SelectTrigger id="et-side" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY">BUY</SelectItem>
                    <SelectItem value="SELL">SELL</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="et-qty">Quantity</FieldLabel>
                <Input id="et-qty" type="number" step="any" {...form.register('qty')} />
                <FieldError errors={[form.formState.errors.qty]} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="et-price">Price (₹)</FieldLabel>
                <Input id="et-price" type="number" step="any" {...form.register('price')} />
                <FieldError errors={[form.formState.errors.price]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="et-date">Traded on</FieldLabel>
                <Input id="et-date" type="date" {...form.register('tradedAt')} />
                <FieldError errors={[form.formState.errors.tradedAt]} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="et-pnl">P&amp;L (₹, negative for a loss)</FieldLabel>
              <Input id="et-pnl" type="number" step="any" {...form.register('pnl')} />
              <FieldError errors={[form.formState.errors.pnl]} />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const BULK_TRADE_ROW_DEFAULT: AddTradeFormValues = {
  symbol: '',
  segment: 'Equity',
  side: 'BUY',
  qty: 0,
  price: 0,
  tradedAt: '',
  pnl: 0,
}

/** The whole tradebook retyped/re-pasted, not diffed row by row — matches `PUT .../trades`'s own
 * replace-all contract exactly. Mirrors `BulkEditHoldingsDialog`. */
function BulkEditTradesDialog({
  trades,
  onSubmit: onSubmitProp,
  open,
  onOpenChange,
}: {
  trades: Trade[]
  onSubmit: (values: CreateCaseTradeInput[]) => Promise<void>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const form = useForm<
    z.input<typeof bulkTradesFormSchema>,
    unknown,
    { rows: BulkTradesFormValues }
  >({
    resolver: zodResolver(bulkTradesFormSchema),
    mode: 'onTouched',
    values: {
      rows: trades.map((t) => ({
        symbol: t.symbol,
        segment: t.segment,
        side: t.side,
        qty: t.qty,
        price: t.price,
        tradedAt: t.tradedAt,
        pnl: t.pnl,
      })),
    },
  })
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'rows' })

  const onSubmit = form.handleSubmit(async ({ rows }) => {
    await onSubmitProp(rows.map(toCreateCaseTradeInput))
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Bulk edit trades</DialogTitle>
            <DialogDescription>
              Replaces the entire tradebook with exactly what&apos;s below — add, remove, or change
              any row.
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto py-4">
            {fields.map((field, index) => {
              const rowErrors = form.formState.errors.rows?.[index]
              return (
                <div key={field.id} className="flex flex-col gap-2 rounded-lg border p-3">
                  <div className="flex items-start gap-2">
                    <Field className="flex-1">
                      <FieldLabel htmlFor={`bt-symbol-${field.id}`}>Symbol</FieldLabel>
                      <Input
                        id={`bt-symbol-${field.id}`}
                        placeholder="Symbol"
                        {...form.register(`rows.${index}.symbol`)}
                      />
                      <FieldError errors={[rowErrors?.symbol]} />
                    </Field>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-muted-foreground mt-6 shrink-0"
                      aria-label="Remove row"
                      onClick={() => remove(index)}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bt-segment-${field.id}`}>Segment</FieldLabel>
                      <Select
                        value={form.watch(`rows.${index}.segment`)}
                        onValueChange={(v) =>
                          form.setValue(`rows.${index}.segment`, v as TradeSegment)
                        }
                      >
                        <SelectTrigger id={`bt-segment-${field.id}`} className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TRADE_SEGMENTS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bt-side-${field.id}`}>Side</FieldLabel>
                      <Select
                        value={form.watch(`rows.${index}.side`)}
                        onValueChange={(v) =>
                          form.setValue(`rows.${index}.side`, v as 'BUY' | 'SELL')
                        }
                      >
                        <SelectTrigger id={`bt-side-${field.id}`} className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BUY">BUY</SelectItem>
                          <SelectItem value="SELL">SELL</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bt-qty-${field.id}`}>Qty</FieldLabel>
                      <Input
                        id={`bt-qty-${field.id}`}
                        type="number"
                        step="any"
                        {...form.register(`rows.${index}.qty`)}
                      />
                      <FieldError errors={[rowErrors?.qty]} />
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bt-price-${field.id}`}>Price</FieldLabel>
                      <Input
                        id={`bt-price-${field.id}`}
                        type="number"
                        step="any"
                        {...form.register(`rows.${index}.price`)}
                      />
                      <FieldError errors={[rowErrors?.price]} />
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bt-date-${field.id}`}>Traded on</FieldLabel>
                      <Input
                        id={`bt-date-${field.id}`}
                        type="date"
                        {...form.register(`rows.${index}.tradedAt`)}
                      />
                      <FieldError errors={[rowErrors?.tradedAt]} />
                    </Field>
                    <Field className="sm:col-span-1">
                      <FieldLabel htmlFor={`bt-pnl-${field.id}`}>P&amp;L</FieldLabel>
                      <Input
                        id={`bt-pnl-${field.id}`}
                        type="number"
                        step="any"
                        {...form.register(`rows.${index}.pnl`)}
                      />
                      <FieldError errors={[rowErrors?.pnl]} />
                    </Field>
                  </div>
                </div>
              )
            })}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="self-start"
              onClick={() => append(BULK_TRADE_ROW_DEFAULT)}
            >
              <Plus className="size-3.5" /> Add row
            </Button>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Saving…' : 'Save all'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AuditAnalysis({
  canEdit,
  hideSummary,
  injectedHoldings,
  onAddHolding,
  onRemoveHolding,
  onEditHolding,
  onReplaceHoldings,
}: {
  canEdit: boolean
  hideSummary: boolean
  /** Real data, fed in by every caller (staff/admin `client-detail.tsx` and the real investor
   * dashboard) — this component no longer fetches anything itself. */
  injectedHoldings?: Holding[]
  /** Injected real mutations — always supplied by the one real (staff-editable) caller. */
  onAddHolding?: (input: CreateCaseHoldingInput) => Promise<void>
  onRemoveHolding?: (id: string) => void
  onEditHolding?: (id: string, input: UpdateCaseHoldingInput) => Promise<void>
  onReplaceHoldings?: (inputs: CreateCaseHoldingInput[]) => Promise<void>
}) {
  const holdings = useMemo(() => injectedHoldings ?? [], [injectedHoldings])
  const isLoading = false
  const [addHoldingOpen, setAddHoldingOpen] = useState(false)
  const [editingHolding, setEditingHolding] = useState<Holding | null>(null)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [holdingFilter, setHoldingFilter] = useState<HoldingCategory | 'all'>('all')
  const [schemeSearch, setSchemeSearch] = useState('')

  // `onAddHolding`/`onRemoveHolding`/`onEditHolding`/`onReplaceHoldings` are always supplied by
  // the one real (staff-editable) caller, `client-detail.tsx` — the add/edit/remove/bulk-edit UI
  // below is gated on their presence rather than falling back to a mock mutation, since no
  // investor-facing caller ever renders with `canEdit=true`.
  const addHolding = async (values: AddHoldingFormValues): Promise<void> => {
    await onAddHolding?.(toCreateCaseHoldingInput(values))
  }
  const removeHolding = (id: string) => onRemoveHolding?.(id)
  const editHolding = async (id: string, values: EditHoldingFormValues): Promise<void> => {
    await onEditHolding?.(id, toCreateCaseHoldingInput(values))
  }
  const replaceHoldings = async (inputs: CreateCaseHoldingInput[]): Promise<void> => {
    await onReplaceHoldings?.(inputs)
  }

  const allocation = useMemo(() => {
    const total = holdings.reduce((s, h) => s + h.currentValue, 0)
    if (!total) return []
    const byCategory = new Map<HoldingCategory, number>()
    for (const h of holdings)
      byCategory.set(h.category, (byCategory.get(h.category) ?? 0) + h.currentValue)
    return [...byCategory.entries()]
      .map(([category, value]) => ({
        category,
        value,
        share: (value / total) * 100,
        fill: CATEGORY_COLOR[category],
      }))
      .sort((a, b) => b.value - a.value)
  }, [holdings])

  if (isLoading) return null

  if (!holdings.length) {
    return (
      <>
        <NoData
          title="No holdings yet"
          description={
            canEdit
              ? 'Key in what you see in the CAS to get started.'
              : 'Holdings appear here once the CAS has been parsed.'
          }
          action={
            canEdit && onAddHolding ? (
              <Button onClick={() => setAddHoldingOpen(true)}>
                <Plus className="size-4" /> Add holding
              </Button>
            ) : undefined
          }
        />
        {canEdit && onAddHolding && (
          <AddHoldingDialog
            onSubmit={addHolding}
            open={addHoldingOpen}
            onOpenChange={setAddHoldingOpen}
          />
        )}
      </>
    )
  }

  const invested = holdings.reduce((s, h) => s + h.investedValue, 0)
  const current = holdings.reduce((s, h) => s + h.currentValue, 0)
  const gain = current - invested
  const gainPct = invested ? (gain / invested) * 100 : 0
  // Value-weighted XIRR — a simple average would over-weight tiny holdings.
  const weightedXirr = current
    ? holdings.reduce((s, h) => s + h.xirr * h.currentValue, 0) / current
    : 0
  const largest = [...holdings].sort((a, b) => b.currentValue - a.currentValue)[0]
  const largestShare = current && largest ? (largest.currentValue / current) * 100 : 0

  // Only the category present on this case's actual holdings — no point offering a "Debt" tab
  // when nothing here is a debt fund. Filtering is a browsing convenience for the card list only;
  // the totals above (Invested/Current/Gain/XIRR, allocation) always reflect the whole portfolio.
  const presentCategories = HOLDING_CATEGORIES.filter((c) => holdings.some((h) => h.category === c))
  const categoryFiltered =
    holdingFilter === 'all' ? holdings : holdings.filter((h) => h.category === holdingFilter)
  const query = schemeSearch.trim().toLowerCase()
  const filteredHoldings = query
    ? categoryFiltered.filter((h) => h.scheme.toLowerCase().includes(query))
    : categoryFiltered
  const filteredInvested = filteredHoldings.reduce((s, h) => s + h.investedValue, 0)
  const filteredCurrent = filteredHoldings.reduce((s, h) => s + h.currentValue, 0)
  const losers = holdings.filter((h) => h.currentValue < h.investedValue)

  return (
    <div className="flex flex-col gap-4">
      {!hideSummary && (
        <div className="@xl/main:grid-cols-2 @4xl/main:grid-cols-4 grid grid-cols-1 gap-3">
          <Metric
            label="Invested"
            value={formatHeadlineCurrency(invested)}
            title={formatCurrency(invested)}
            sub={`${holdings.length} schemes`}
            icon={Wallet}
            iconAccent="var(--chart-2)"
            info="The total amount you've put in across all your holdings."
          />
          <Metric
            label="Current Value"
            value={formatHeadlineCurrency(current)}
            title={formatCurrency(current)}
            icon={TrendingUp}
            iconAccent="var(--chart-1)"
            info="What your holdings are worth today, at current market prices."
          />
          <Metric
            label="Gain / Loss"
            value={<Signed value={gain} compact />}
            title={signedTitle(gain)}
            sub={<Signed value={gainPct} asPercent />}
            icon={LineChart}
            iconAccent="var(--chart-positive)"
            info="The difference between what you've invested and what it's worth now."
          />
          <Metric
            label="XIRR"
            value={pct(weightedXirr)}
            sub="Value-weighted"
            icon={PieChartIcon}
            iconAccent="var(--chart-4)"
            info="Your holding-weighted annualised rate of return, blended across every scheme."
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        <Card>
          <CardHeader className="flex flex-row items-center gap-1.5 space-y-0">
            <CardTitle className="text-sm">Asset Allocation</CardTitle>
            <InfoPopover
              contentClassName="w-56 text-center text-sm"
              trigger={
                <button
                  type="button"
                  aria-label="How is this calculated?"
                  className="text-muted-foreground"
                >
                  <Info className="size-3.5" />
                </button>
              }
            >
              Split of your current holdings by asset category, weighted by today&apos;s value.
            </InfoPopover>
          </CardHeader>
          {/* A card per category rather than full-width rows: as rows, the bar stretched the whole
              card while the amount sat stranded at the far right, so the two things you compare —
              share and value — were the furthest apart on screen. */}
          <CardContent className="@xl/main:grid-cols-2 @4xl/main:grid-cols-3 grid grid-cols-1 gap-3">
            {allocation.map((a) => {
              const CategoryIcon = CATEGORY_ICON[a.category]
              return (
                <div key={a.category} className="flex flex-col gap-3 rounded-xl border p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className="flex size-9 shrink-0 items-center justify-center rounded-lg"
                        style={{
                          backgroundColor: `color-mix(in oklch, ${a.fill} 14%, transparent)`,
                          color: a.fill,
                        }}
                      >
                        <CategoryIcon className="size-4" />
                      </span>
                      <span className="truncate text-sm font-medium">{a.category}</span>
                    </div>
                    <Badge
                      className="shrink-0 border-transparent font-semibold"
                      style={{
                        backgroundColor: `color-mix(in oklch, ${a.fill} 14%, transparent)`,
                        color: a.fill,
                      }}
                    >
                      {pct(a.share)}
                    </Badge>
                  </div>

                  <p className="text-lg font-bold tabular-nums leading-none">
                    {formatCurrency(a.value)}
                  </p>

                  {/* --border-primary, not --muted: the muted surfaces are within a few RGB points
                      of white in the light theme, so the empty track disappears on a white card. */}
                  <div
                    className="h-1.5 w-full overflow-hidden rounded-full"
                    style={{ backgroundColor: 'var(--border-primary)' }}
                    role="img"
                    aria-label={`${a.category}: ${pct(a.share)} of holdings, ${formatCurrency(a.value)}.`}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${a.share}%`, backgroundColor: a.fill }}
                    />
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>

        {largestShare > CONCENTRATION_THRESHOLD && largest && (
          <div className="mt-1 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span>
              <strong>{pct(largestShare)}</strong> sits in a single scheme ({largest.scheme}) —
              worth raising on the call.
            </span>
          </div>
        )}
        {losers.length > 0 && (
          <p className="text-muted-foreground text-xs">
            {losers.length} of {holdings.length} schemes are below cost.
          </p>
        )}
      </div>

      {canEdit && onAddHolding && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setAddHoldingOpen(true)}>
              <Plus className="size-3.5" /> Add holding
            </Button>
            {onReplaceHoldings && (
              <Button size="sm" variant="outline" onClick={() => setBulkEditOpen(true)}>
                <PencilLine className="size-3.5" /> Bulk edit
              </Button>
            )}
          </div>
          <AddHoldingDialog
            onSubmit={addHolding}
            open={addHoldingOpen}
            onOpenChange={setAddHoldingOpen}
          />
          <EditHoldingDialog
            holding={editingHolding}
            onSubmit={editHolding}
            open={editingHolding !== null}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) setEditingHolding(null)
            }}
          />
          {onReplaceHoldings && (
            <BulkEditHoldingsDialog
              holdings={holdings}
              onSubmit={replaceHoldings}
              open={bulkEditOpen}
              onOpenChange={setBulkEditOpen}
            />
          )}
        </div>
      )}

      {/* Cards below md — a dense table crushes on mobile. Table at md+, since with real
          columns to scan (units/invested/current/gain/XIRR side by side) it reads faster
          than cards once there's room for it. */}
      <div className="@lg/main:flex-row @lg/main:items-center @lg/main:justify-between flex flex-col gap-3">
        {presentCategories.length > 1 ? (
          <Tabs
            value={holdingFilter}
            onValueChange={(v) => setHoldingFilter(v as HoldingCategory | 'all')}
          >
            <TabsList variant="line">
              <TabsTrigger value="all">All</TabsTrigger>
              {presentCategories.map((c) => (
                <TabsTrigger key={c} value={c}>
                  {c}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : (
          <div />
        )}
        <div className="@lg/main:w-[240px] relative">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
          <Input
            value={schemeSearch}
            onChange={(e) => setSchemeSearch(e.target.value)}
            placeholder="Search schemes…"
            className="pl-8"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 md:hidden">
        {filteredHoldings.length === 0 && (
          <p className="text-muted-foreground rounded-lg border border-dashed py-6 text-center text-sm">
            No schemes match &quot;{schemeSearch}&quot;.
          </p>
        )}
        {filteredHoldings.map((h) => (
          <div key={h.id} className="flex items-start gap-3 rounded-lg border p-3">
            <SchemeMark initial={h.scheme.charAt(0)} color={CATEGORY_COLOR[h.category]} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium leading-snug">{h.scheme}</p>
                <div className="-mr-1 -mt-1 flex shrink-0 items-center">
                  {canEdit && onEditHolding && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-muted-foreground size-6"
                      aria-label={`Edit ${h.scheme}`}
                      onClick={() => setEditingHolding(h)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                  )}
                  {canEdit && onRemoveHolding && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-muted-foreground size-6"
                      aria-label={`Remove ${h.scheme}`}
                      onClick={() => removeHolding(h.id)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Badge variant="secondary">{h.category}</Badge>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {h.units.toFixed(2)} units
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                <span className="text-muted-foreground">
                  Invested{' '}
                  <span className="text-foreground tabular-nums">
                    {formatCurrency(h.investedValue)}
                  </span>
                </span>
                <span className="text-muted-foreground">
                  Current{' '}
                  <span className="text-foreground tabular-nums">
                    {formatCurrency(h.currentValue)}
                  </span>
                </span>
                <span className="text-muted-foreground">
                  Gain <Signed value={h.currentValue - h.investedValue} />
                </span>
                <span className="text-muted-foreground">
                  XIRR <Signed value={h.xirr} asPercent />
                </span>
              </div>
            </div>
          </div>
        ))}
        <div className="bg-muted/40 flex items-center justify-between rounded-lg border px-3 py-2.5 text-xs">
          <span className="font-medium">
            {filteredHoldings.length} scheme{filteredHoldings.length !== 1 ? 's' : ''}
            {holdingFilter !== 'all' ? ` (${holdingFilter})` : ''}
          </span>
          <span className="tabular-nums">
            {formatCurrency(filteredInvested)} → {formatCurrency(filteredCurrent)} (
            <Signed value={filteredCurrent - filteredInvested} />)
          </span>
        </div>
      </div>

      <div className="hidden overflow-hidden rounded-lg border md:block">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Scheme</TableHead>
              <TableHead>Units</TableHead>
              <TableHead className="text-right">Invested</TableHead>
              <TableHead className="text-right">Current</TableHead>
              <TableHead className="text-right">Gain / Loss</TableHead>
              <TableHead className="text-right">XIRR</TableHead>
              {canEdit && <TableHead className="w-16" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredHoldings.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={canEdit ? 7 : 6}
                  className="text-muted-foreground h-24 text-center"
                >
                  No schemes match &quot;{schemeSearch}&quot;.
                </TableCell>
              </TableRow>
            )}
            {filteredHoldings.map((h) => (
              <TableRow key={h.id}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <SchemeMark
                      initial={h.scheme.charAt(0)}
                      color={CATEGORY_COLOR[h.category]}
                      size={7}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-snug">{h.scheme}</p>
                      <Badge variant="secondary" className="mt-0.5">
                        {h.category}
                      </Badge>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {h.units.toFixed(2)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrency(h.investedValue)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrency(h.currentValue)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  <Signed value={h.currentValue - h.investedValue} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  <Signed value={h.xirr} asPercent />
                </TableCell>
                {canEdit && (onEditHolding || onRemoveHolding) && (
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      {onEditHolding && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground size-7"
                          aria-label={`Edit ${h.scheme}`}
                          onClick={() => setEditingHolding(h)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                      )}
                      {onRemoveHolding && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground size-7"
                          aria-label={`Remove ${h.scheme}`}
                          onClick={() => removeHolding(h.id)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="bg-muted/40 flex items-center justify-between border-t px-3 py-2.5 text-xs">
          <span className="font-medium">
            {filteredHoldings.length} scheme{filteredHoldings.length !== 1 ? 's' : ''}
            {holdingFilter !== 'all' ? ` (${holdingFilter})` : ''}
          </span>
          <span className="tabular-nums">
            {formatCurrency(filteredInvested)} → {formatCurrency(filteredCurrent)} (
            <Signed value={filteredCurrent - filteredInvested} />)
          </span>
        </div>
      </div>
    </div>
  )
}

function TradesAnalysis({
  canEdit,
  hideSummary,
  injectedTrades,
  onAddTrade,
  onRemoveTrade,
  onEditTrade,
  onReplaceTrades,
}: {
  canEdit: boolean
  hideSummary: boolean
  /** Real data — see `AuditAnalysis`'s `injectedHoldings` for why. */
  injectedTrades?: Trade[]
  /** Injected real mutations — see `AuditAnalysis`'s `onAddHolding`/`onRemoveHolding` for why. */
  onAddTrade?: (input: CreateCaseTradeInput) => Promise<void>
  onRemoveTrade?: (id: string) => void
  onEditTrade?: (id: string, input: UpdateCaseTradeInput) => Promise<void>
  onReplaceTrades?: (inputs: CreateCaseTradeInput[]) => Promise<void>
}) {
  const trades = injectedTrades ?? []
  const isLoading = false
  const [addTradeOpen, setAddTradeOpen] = useState(false)
  const [editingTrade, setEditingTrade] = useState<Trade | null>(null)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [tradeFilter, setTradeFilter] = useState<TradeSegment | 'all'>('all')

  // `onAddTrade`/`onRemoveTrade`/`onEditTrade`/`onReplaceTrades` are always supplied by the one
  // real (staff-editable) caller — see `AuditAnalysis`'s identical `addHolding`/`removeHolding`
  // comment for why there's no mock fallback here.
  const addTrade = async (values: AddTradeFormValues): Promise<void> => {
    await onAddTrade?.(toCreateCaseTradeInput(values))
  }
  const removeTrade = (id: string) => onRemoveTrade?.(id)
  const editTrade = async (id: string, values: EditTradeFormValues): Promise<void> => {
    await onEditTrade?.(id, toCreateCaseTradeInput(values))
  }
  const replaceTrades = async (inputs: CreateCaseTradeInput[]): Promise<void> => {
    await onReplaceTrades?.(inputs)
  }

  if (isLoading) return null

  if (!trades.length) {
    return (
      <>
        <NoData
          title="No trades yet"
          description={
            canEdit
              ? 'Key in what you see in the tradebook to get started.'
              : 'Trades appear here once the tradebook has been parsed.'
          }
          action={
            canEdit && onAddTrade ? (
              <Button onClick={() => setAddTradeOpen(true)}>
                <Plus className="size-4" /> Add trade
              </Button>
            ) : undefined
          }
        />
        {canEdit && onAddTrade && (
          <AddTradeDialog onSubmit={addTrade} open={addTradeOpen} onOpenChange={setAddTradeOpen} />
        )}
      </>
    )
  }

  const net = trades.reduce((s, t) => s + t.pnl, 0)
  const wins = trades.filter((t) => t.pnl > 0)
  const winRate = (wins.length / trades.length) * 100
  const turnover = trades.reduce((s, t) => s + t.qty * t.price, 0)
  const bySegment = new Map<string, number>()
  for (const t of trades) bySegment.set(t.segment, (bySegment.get(t.segment) ?? 0) + 1)
  const biggestLoss = [...trades].sort((a, b) => a.pnl - b.pnl)[0]

  // Same reasoning as the holdings filter: only offer segments this case's tradebook actually
  // has. Filtering only affects the card list and its own summary row, not the metrics above.
  const presentSegments = TRADE_SEGMENTS.filter((s) => trades.some((t) => t.segment === s))
  const filteredTrades =
    tradeFilter === 'all' ? trades : trades.filter((t) => t.segment === tradeFilter)
  const filteredNet = filteredTrades.reduce((s, t) => s + t.pnl, 0)

  return (
    <div className="flex flex-col gap-4">
      {!hideSummary && (
        <div className="@xl/main:grid-cols-2 @4xl/main:grid-cols-4 grid grid-cols-1 gap-3">
          <Metric
            label="Net P&L"
            value={<Signed value={net} compact />}
            title={signedTitle(net)}
            sub={`${trades.length} trades`}
            icon={TrendingUp}
            iconAccent="var(--chart-1)"
          />
          <Metric
            label="Win Rate"
            value={pct(winRate)}
            sub={`${wins.length} of ${trades.length} profitable`}
            icon={LineChart}
            iconAccent="var(--chart-positive)"
          />
          <Metric
            label="Turnover"
            value={formatHeadlineCurrency(turnover)}
            title={formatCurrency(turnover)}
            sub="Total traded value"
            icon={Wallet}
            iconAccent="var(--chart-4)"
          />
          <Metric
            label="Segments"
            value={[...bySegment.keys()].join(', ')}
            sub={[...bySegment.entries()].map(([s, n]) => `${s} ${n}`).join(' · ')}
            icon={PieChartIcon}
            iconAccent="var(--chart-2)"
          />
        </div>
      )}

      {biggestLoss && biggestLoss.pnl < 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Largest single loss is <strong>{formatCurrency(Math.abs(biggestLoss.pnl))}</strong> on{' '}
            {biggestLoss.symbol} ({biggestLoss.segment}).
          </span>
        </div>
      )}

      {canEdit && onAddTrade && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setAddTradeOpen(true)}>
              <Plus className="size-3.5" /> Add trade
            </Button>
            {onReplaceTrades && (
              <Button size="sm" variant="outline" onClick={() => setBulkEditOpen(true)}>
                <PencilLine className="size-3.5" /> Bulk edit
              </Button>
            )}
          </div>
          <AddTradeDialog onSubmit={addTrade} open={addTradeOpen} onOpenChange={setAddTradeOpen} />
          <EditTradeDialog
            trade={editingTrade}
            onSubmit={editTrade}
            open={editingTrade !== null}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) setEditingTrade(null)
            }}
          />
          {onReplaceTrades && (
            <BulkEditTradesDialog
              trades={trades}
              onSubmit={replaceTrades}
              open={bulkEditOpen}
              onOpenChange={setBulkEditOpen}
            />
          )}
        </div>
      )}

      {/* Cards below md, table at md+ — same reasoning as holdings. "Logo" here is a
          segment-colored initial avatar, not a real broker/symbol icon. */}
      {presentSegments.length > 1 && (
        <Tabs value={tradeFilter} onValueChange={(v) => setTradeFilter(v as TradeSegment | 'all')}>
          <TabsList variant="line">
            <TabsTrigger value="all">All</TabsTrigger>
            {presentSegments.map((s) => (
              <TabsTrigger key={s} value={s}>
                {s}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      <div className="grid grid-cols-1 gap-2 md:hidden">
        {filteredTrades.map((t) => (
          <div key={t.id} className="flex items-start gap-3 rounded-lg border p-3">
            <SchemeMark initial={t.symbol.charAt(0)} color={SEGMENT_COLOR[t.segment]} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium leading-snug">{t.symbol}</p>
                <div className="-mr-1 -mt-1 flex shrink-0 items-center">
                  {canEdit && onEditTrade && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-muted-foreground size-6"
                      aria-label={`Edit ${t.symbol} trade`}
                      onClick={() => setEditingTrade(t)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                  )}
                  {canEdit && onRemoveTrade && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-muted-foreground size-6"
                      aria-label={`Remove ${t.symbol} trade`}
                      onClick={() => removeTrade(t.id)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Badge variant="secondary">{t.segment}</Badge>
                <span className="text-muted-foreground text-xs">{t.side}</span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {formatDate(t.tradedAt)}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                <span className="text-muted-foreground">
                  Qty <span className="text-foreground tabular-nums">{t.qty}</span>
                </span>
                <span className="text-muted-foreground">
                  Price{' '}
                  <span className="text-foreground tabular-nums">{formatCurrency(t.price)}</span>
                </span>
                <span className="text-muted-foreground">
                  P&amp;L <Signed value={t.pnl} />
                </span>
              </div>
            </div>
          </div>
        ))}
        <div className="bg-muted/40 flex items-center justify-between rounded-lg border px-3 py-2.5 text-xs">
          <span className="font-medium">
            {filteredTrades.length} trade{filteredTrades.length !== 1 ? 's' : ''}
            {tradeFilter !== 'all' ? ` (${tradeFilter})` : ''}
          </span>
          <span className="tabular-nums">
            net <Signed value={filteredNet} />
          </span>
        </div>
      </div>

      <div className="hidden overflow-hidden rounded-lg border md:block">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Symbol</TableHead>
              <TableHead>Side</TableHead>
              <TableHead>Traded on</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">P&amp;L</TableHead>
              {canEdit && <TableHead className="w-16" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTrades.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <SchemeMark
                      initial={t.symbol.charAt(0)}
                      color={SEGMENT_COLOR[t.segment]}
                      size={7}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-snug">{t.symbol}</p>
                      <Badge variant="secondary" className="mt-0.5">
                        {t.segment}
                      </Badge>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{t.side}</TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {formatDate(t.tradedAt)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{t.qty}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(t.price)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  <Signed value={t.pnl} />
                </TableCell>
                {canEdit && (onEditTrade || onRemoveTrade) && (
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      {onEditTrade && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground size-7"
                          aria-label={`Edit ${t.symbol} trade`}
                          onClick={() => setEditingTrade(t)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                      )}
                      {onRemoveTrade && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground size-7"
                          aria-label={`Remove ${t.symbol} trade`}
                          onClick={() => removeTrade(t.id)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="bg-muted/40 flex items-center justify-between border-t px-3 py-2.5 text-xs">
          <span className="font-medium">
            {filteredTrades.length} trade{filteredTrades.length !== 1 ? 's' : ''}
            {tradeFilter !== 'all' ? ` (${tradeFilter})` : ''}
          </span>
          <span className="tabular-nums">
            net <Signed value={filteredNet} />
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * The extracted CAS / tradebook rendered as analysis rather than a document — the numbers an
 * advisor needs in front of them on the call. Shared by staff and admin so the two can never show
 * a client different figures. `canEdit` is staff's own seam for turning a submitted request into
 * that analysis: keying in what they see in the document, line by line, since nothing here parses
 * a real file. Investor-facing callers simply omit it.
 *
 * `hideSummary` drops the per-flow metric grid. A page that renders its own hero row above this
 * component with the same figures should pass it, so Net P&L/Win Rate/Turnover (or Invested/
 * Current/Gain/XIRR) don't appear twice on one screen.
 */
export function CaseAnalysis({
  caseRecord,
  canEdit = false,
  hideSummary = false,
  injectedHoldings,
  injectedTrades,
  onAddHolding,
  onRemoveHolding,
  onEditHolding,
  onReplaceHoldings,
  onAddTrade,
  onRemoveTrade,
  onEditTrade,
  onReplaceTrades,
}: {
  caseRecord: CaseRecord
  canEdit?: boolean
  hideSummary?: boolean
  /** Pre-fetched real data — see `AuditAnalysis`'s own doc comment for why this exists. */
  injectedHoldings?: Holding[]
  injectedTrades?: Trade[]
  /** Injected real mutations — see `AuditAnalysis`'s own doc comment for why this exists. */
  onAddHolding?: (input: CreateCaseHoldingInput) => Promise<void>
  onRemoveHolding?: (id: string) => void
  onEditHolding?: (id: string, input: UpdateCaseHoldingInput) => Promise<void>
  onReplaceHoldings?: (inputs: CreateCaseHoldingInput[]) => Promise<void>
  onAddTrade?: (input: CreateCaseTradeInput) => Promise<void>
  onRemoveTrade?: (id: string) => void
  onEditTrade?: (id: string, input: UpdateCaseTradeInput) => Promise<void>
  onReplaceTrades?: (inputs: CreateCaseTradeInput[]) => Promise<void>
}) {
  return (
    <ErrorBoundary label="this analysis" resetKeys={[caseRecord.id]}>
      {caseRecord.flow === 'audit' ? (
        <AuditAnalysis
          canEdit={canEdit}
          hideSummary={hideSummary}
          injectedHoldings={injectedHoldings}
          onAddHolding={onAddHolding}
          onRemoveHolding={onRemoveHolding}
          onEditHolding={onEditHolding}
          onReplaceHoldings={onReplaceHoldings}
        />
      ) : (
        <TradesAnalysis
          canEdit={canEdit}
          hideSummary={hideSummary}
          injectedTrades={injectedTrades}
          onAddTrade={onAddTrade}
          onRemoveTrade={onRemoveTrade}
          onEditTrade={onEditTrade}
          onReplaceTrades={onReplaceTrades}
        />
      )}
    </ErrorBoundary>
  )
}
